import { Component, computed, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { SCA } from './core/tokens/sca.token';
import { NavigationFailureService } from './core/services/navigation-failure.service';
import { ScaModalComponent } from './shared/sca-modal/sca-modal.component';

/**
 * Application root.
 *
 * ## Why the SCA modal is mounted HERE (EM-1445)
 * `SCA.challenge(...)` parks a `PendingScaChallenge` on a signal and returns a Promise that
 * `<lendox-sca-modal>` is responsible for settling. The modal used to be mounted at the SME
 * borrower shell root — the ONE shell that happened to need it first — so every SCA-gated action
 * reachable from any other host awaited a promise with nobody to fulfil it: the caller hung on
 * "Saving…" forever, with no dialog, no request and no timeout. That hit
 * `/lender/sm/representatives` and `/ed/representatives` (the shared representatives panel),
 * `/lender/sm/account`, and the standalone account page behind `/sme/account`, `/lender/account`
 * and `/ed/account`.
 *
 * `SCA` is provided app-wide and ANY component may call `challenge(...)`, so the modal that answers
 * it belongs at the same scope — not once per shell, which is precisely the arrangement that made
 * it possible to forget. One mount, at the root, covers every current and future route.
 *
 * ## Why `@defer`, not a plain mount
 * The root is eagerly loaded, so mounting the modal directly pulled it — and `ReactiveFormsModule`
 * plus the Transloco chain it imports — into the INITIAL bundle: +63 kB raw on every page load,
 * including the landing page, to serve a dialog most sessions never open. `@defer (when …)` keeps
 * the single root mount but leaves the component in its own lazy chunk, fetched the first time a
 * challenge is actually raised. The challenge is already awaiting a promise across at least one
 * network round-trip (`/me/sca/send`), so the chunk fetch adds nothing a user can perceive.
 *
 * Once loaded the modal's own effect sees the already-pending challenge and opens on it, and it
 * costs nothing thereafter: its whole template sits inside `@if (pending())`.
 *
 * `@error` covers the case that would otherwise re-create this very bug in rarer form: if the chunk
 * cannot be fetched (offline, or a tab left open across a redeploy so the hashed name is gone),
 * nothing would settle the parked promise and the caller would hang on "Saving…" again. The error
 * branch says so and lets the user abandon the step-up — every `challenge()` caller already clears
 * its busy flag in a `catch`.
 *
 * Deliberately NO `prefetch`: a deferred block that fails is FAILED for the rest of the page-load
 * (Angular never re-attempts the import), so prefetching would let a blip while the app sits idle
 * poison the block and greet the user's first Save with the error branch on a network that has
 * since recovered. Loading on the trigger fetches at the one moment the user is demonstrably online.
 *
 * The error banner is itself wrapped in `@if (scaPending())`: the Error state is permanent, so an
 * unconditional banner would sit on every route for the rest of the session — including after the
 * user abandons the challenge. Tying it to a genuinely stranded challenge makes it self-clearing.
 */
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, ScaModalComponent, TranslocoPipe],
  template: `<router-outlet />
    <!-- EM-1634 — a lazy route whose chunk cannot be fetched abandons the navigation silently:
         the user clicks Open and nothing happens.
         EM-1641 — and it matters WHY. The reload is offered either way, from the first frame:
         withholding it on an inference would strand a user whose reload WOULD have worked, which is
         this same bug from the other side. What the verdict changes is the wording — when the tab is
         already on the release being served, the banner adds what it means if the reload does not
         help, which is the one thing the lender was never told. -->
    @if (chunkLoadFailed()) {
      <div class="route-unavailable" role="alert" data-testid="route-chunk-banner">
        @if (looksLikeIncompleteBuild()) {
          <span data-testid="route-chunk-incomplete-build">
            {{ 'error.routeChunkMissingFromBuild' | transloco }}
          </span>
        } @else {
          <span data-testid="route-chunk-stale-tab">
            {{ 'error.routeChunkUnavailable' | transloco }}
          </span>
        }
        <button type="button" data-testid="route-chunk-reload" (click)="reloadForNewVersion()">
          {{ 'common.actions.reload' | transloco }}
        </button>
        <button
          type="button"
          class="route-unavailable__dismiss"
          data-testid="route-chunk-close"
          (click)="dismissRouteFailure()"
        >
          {{ 'common.actions.close' | transloco }}
        </button>
      </div>
    }
    @defer (when scaPending()) {
      <lendox-sca-modal />
    } @error {
      @if (scaPending()) {
        <div class="sca-unavailable">
          <span role="alert">{{ 'sca.errors.unavailable' | transloco }}</span>
          <button type="button" (click)="abortScaChallenge()">
            {{ 'sca.errors.unavailableAbort' | transloco }}
          </button>
        </div>
      }
    }`,
  styles: [
    `.sca-unavailable {
      position: fixed;
      left: 50%;
      bottom: 24px;
      transform: translateX(-50%);
      z-index: 1300;
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      border-radius: 10px;
      background: #fee2e2;
      border: 1px solid #ef4444;
      color: #b91c1c;
      font-size: 13px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
    }

    /* Same treatment as .sca-unavailable — one visual language for "a chunk did not load",
       sitting just above it so the two can never overlap if both ever appear at once. */
    .route-unavailable {
      position: fixed;
      left: 50%;
      bottom: 80px;
      transform: translateX(-50%);
      z-index: 1300;
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      border-radius: 10px;
      background: #fee2e2;
      border: 1px solid #ef4444;
      color: #b91c1c;
      font-size: 13px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
    }

    .route-unavailable button {
      border: 1px solid currentColor;
      background: transparent;
      color: inherit;
      border-radius: 6px;
      padding: 4px 10px;
      font: inherit;
      cursor: pointer;
    }

    .sca-unavailable button {
      border: 1px solid currentColor;
      background: transparent;
      color: inherit;
      border-radius: 6px;
      padding: 4px 10px;
      font: inherit;
      cursor: pointer;
    }`,
  ],
})
export class AppComponent {
  private readonly sca = inject(SCA);
  private readonly navigationFailure = inject(NavigationFailureService);

  /** True once a lazy route's chunk failed to load — see NavigationFailureService. */
  protected readonly chunkLoadFailed = this.navigationFailure.chunkLoadFailed;

  /**
   * True when the evidence says this tab is already on the release being served (EM-1641), which
   * selects the wording that tells the user what it means if the reload does not help. It never
   * removes the reload — see NavigationFailureService.
   */
  protected readonly looksLikeIncompleteBuild = this.navigationFailure.looksLikeIncompleteBuild;

  /** Drives the `@defer` above — true from the moment any pillar calls `SCA.challenge(...)`. */
  protected readonly scaPending = computed(() => this.sca.pending() !== null);

  /**
   * Releases a challenge that can never be answered because the modal's chunk failed to load.
   * Rejecting is what unblocks the caller — the alternative is the EM-1445 hang all over again —
   * and it also clears `scaPending()`, which is what dismisses the banner above.
   */
  protected abortScaChallenge(): void {
    this.sca.pending()?.reject({ chunkLoadFailed: true });
  }

  /** Picks up the server's current index.html, which is what a missing hashed chunk implies. */
  protected reloadForNewVersion(): void {
    this.navigationFailure.reload();
  }

  /** Lets the user keep working on the page they are on rather than forcing the reload. */
  protected dismissRouteFailure(): void {
    this.navigationFailure.dismiss();
  }
}
